/**
 * MagicLinkVerify — /auth/verify
 *
 * Reads `id` and `token` from the URL search params.
 * - Missing either → immediate error panel, no API call.
 * - Otherwise → on mount calls verifyMagicLink(id, token).
 *   Success: adoptSession(response) + navigate to /results/:valuationId.
 *   Failure: error panel with "Start over" link.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { verifyMagicLink } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import './Auth.css';

function MagicLinkVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const id = searchParams.get('id');
  const token = searchParams.get('token');

  const [status, setStatus] = useState('pending'); // 'pending' | 'verifying' | 'error'
  const [errorMsg, setErrorMsg] = useState(null);

  // Guard against React StrictMode's double-invoke: only make one verify call
  // per component instance regardless of how many times the effect fires.
  // The ref persists through the StrictMode unmount/remount cycle.
  const verifyCalledRef = useRef(false);

  useEffect(() => {
    if (!id || !token) {
      setStatus('error');
      setErrorMsg('missing');
      return;
    }

    // Prevent the StrictMode double-fire from consuming the single-use token twice.
    if (verifyCalledRef.current) return;
    verifyCalledRef.current = true;

    setStatus('verifying');

    verifyMagicLink(id, token)
      .then((response) => {
        // Always adopt the session and navigate on success — navigation is safe
        // to invoke even during StrictMode's artificial unmount/remount cycle.
        adoptSession(response);
        navigate(`/results/${response.valuationId}`, { replace: true });
      })
      .catch(() => {
        setStatus('error');
        setErrorMsg('expired');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'error' && errorMsg === 'missing') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Invalid Link</h1>
          </div>
          <div className="error-message" role="alert">
            <div>
              <p className="error-title">Missing link parameters</p>
              <p className="error-text">
                This link is missing required information. Please use the link
                from your email exactly as sent.
              </p>
            </div>
          </div>
          <div className="auth-switch" style={{ marginTop: '1.5rem' }}>
            <Link to="/valuation" className="link">
              Start over
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error' && errorMsg === 'expired') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Link Expired</h1>
          </div>
          <div className="error-message" role="alert">
            <div>
              <p className="error-title">
                This link is invalid or has expired — links are single-use and
                last 30 minutes.
              </p>
              <p className="error-text">
                Request a new link by starting the valuation again.
              </p>
            </div>
          </div>
          <div className="auth-switch" style={{ marginTop: '1.5rem' }}>
            <Link to="/valuation" className="link">
              Start over
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // 'verifying' or 'pending' states — show spinner
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Verifying your link…</h1>
          <p>Please wait while we confirm your access.</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
          <span className="spinner" style={{ width: 32, height: 32, borderWidth: 3, borderColor: '#667eea33', borderTopColor: '#667eea' }} />
        </div>
      </div>
    </div>
  );
}

export default MagicLinkVerify;
